export interface RollsetPreparedRow {
	Id: number;
	Name: string | null;
	StatusCode: string | null;
	StatusName: string | null;
	CartridgeId: number | null;
	CartridgeName: string | null;
	BarrelId: number | null;
	TopRollId: number | null;
	TopRollName: string | null;
	TopRollDiameter: number | null;
	BottomRollId: number | null;
	BottomRollName: string | null;
	BottomRollDiameter: number | null;
	Diameter: number | null;
	PreparedStandId: number | null;
	StarterPassId: number | null;
	StarterPassName: string | null;
	StarterPassPosition: number | null;
	Timestamp: string | null;
	Description: string | null;
	MillSizeId: number | null;
	PassId: number | null;
	Comments: string | null;
}

export interface PreparedStandRow extends RollsetPreparedRow {
	StandName: string;
	StandOrder: number;
	IsPlaceholder: boolean;
	IsSecondary: boolean;
	/** Total count of prepared rollsets for this stand (including the primary). */
	AlternatesCount: number;
	/** True when this is the primary row and the stand has ≥ 2 prepared rollsets. */
	HasAlternates: boolean;
}

export interface BarrelLayoutRow {
	IdBottom: number | null;
	IdTop: number | null;
	RollsetId: number;
	BarrelId: number | null;
	BarrelName: string | null;
	PassId: number | null;
	PassName: string | null;
	PassPosition: number | null;
	PassTypeId: number | null;
	PassTypeName: string | null;
	PassShapeId: number | null;
	PassShapeName: string | null;
	PassStatusCode: string | null;
	ScheduledTonnage: number | null;
	RolledTonnage: number | null;
	StandId: number | null;
	StandName: string | null;
}

export interface Stand {
	Id: number;
	Name: string;
	StandType: string;
	StandNumber: number;
	StandOrder: number;
	MillSizeId: number | null;
	MillSizeName: string | null;
	GroupName: string | null;
	Active: boolean;
}

export interface CartridgeOption {
	Id: number;
	Name: string;
	MillSizeId: number | null;
	MillSizeName: string | null;
	Active: boolean;
	/** The rollset staged against the cartridge — what picking it opens. */
	RollsetId: number | null;
	RollsetName: string | null;
	StandId: number | null;
	StandName: string | null;
	StandOrder: number | null;
	/** AVAILABLE | PREPAREDFORLINE | INUSE, read off the staged rollset. */
	StatusCode: string | null;
	StatusName: string | null;
}

/** One match of the rollset typeahead. */
export interface RollsetSearchResult {
	Id: number;
	Name: string;
	StatusCode: string | null;
	StatusName: string | null;
	StandNumber: number | null;
	StandName: string | null;
	TopRollName: string | null;
	BottomRollName: string | null;
	CartridgeId: number | null;
	CartridgeName: string | null;
}

export interface RollsetDetail {
	Id: number;
	Name: string;
	StatusCode: string | null;
	StatusName: string | null;
	StandId: number | null;
	StandNumber: number | null;
	StandName: string | null;
	MillSizeId: number | null;
	MillSizeName: string | null;
	PassId: number | null;
	PassName: string | null;
	TopRollId: number | null;
	TopRollName: string | null;
	TopRollDiameter: number | null;
	BottomRollId: number | null;
	BottomRollName: string | null;
	BottomRollDiameter: number | null;
	CartridgeId: number | null;
	CartridgeName: string | null;
	BarrelId: number | null;
	BarrelName: string | null;
	PassStatusCode: string | null;
	PassStatusName: string | null;
	Description: string | null;
	Comments: string | null;
	PreparedForLineTimestampString: string | null;
}

/**
 * What the detail panel renders. A rollset reaches the panel either from the
 * master grid or from one of the two filters, and only the first has a grid
 * row, so the panel reads this instead of either source directly.
 */
export interface RollsetView {
	Id: number;
	Name: string | null;
	StandName: string | null;
	CartridgeName: string | null;
	TopRollName: string | null;
	BottomRollName: string | null;
	TopRollDiameter: number | null;
	BottomRollDiameter: number | null;
	BarrelName: string | null;
	Timestamp: string | null;
}

/**
 * Client-side computed rollset "assignment status" (legacy convention):
 *   - ASSIGNED : both top and bottom rolls assigned
 *   - PARTIAL  : only one of the two rolls assigned
 *   - AVAILABLE: no rolls assigned
 *
 * This is distinct from `StatusCode` (operational status: AVAILABLE /
 * PREPAREDFORLINE / INUSE) which is stored in catRollsets.
 */
export type RollsetAssignmentStatus = "ASSIGNED" | "PARTIAL" | "AVAILABLE";

export const computeAssignmentStatus = (
	topRollName: string | null,
	bottomRollName: string | null,
): RollsetAssignmentStatus => {
	const hasTop = !!topRollName;
	const hasBottom = !!bottomRollName;
	if (hasTop && hasBottom) return "ASSIGNED";
	if (hasTop || hasBottom) return "PARTIAL";
	return "AVAILABLE";
};

export const assignmentStatusName = (
	status: RollsetAssignmentStatus,
): string => {
	switch (status) {
		case "ASSIGNED":
			return "Assigned";
		case "PARTIAL":
			return "Partial";
		case "AVAILABLE":
			return "Available";
	}
};
